import {
    getConfig,
    AppConfig,
    PushNotificationType,
} from '@superbet-group/web.lib.config';
import { delay } from '@superbet-group/web.lib.utilities';
import { SubscriptionLike } from 'rxjs';
import {
    ActionContext,
    Commit,
    Dispatch,
} from 'vuex';

import NotificationActionError from '@core/errors/user/NotificationActionError';
import BonusProvider from '@core/models/bonus/BonusProvider';
import User from '@core/models/user/User';
import {
    shouldChooseGdprConsent,
    deleteIdCardVerificationPending,
} from '@core/models/user/UserDataService';
import Message from '@core/models/user/pushNotifications/Message';
import { UserService } from '@core/services/user/UserService';
import {
    NotificationType,
    PushMessageType,
} from '@models/user/enums';
import { shellLocalConfig } from '@src/config/shell/localConfig';
import StoreUtil from '@store/StoreUtil';
import modalCodes from '@store/modules/ui/shared/modal/modalCodes';
import { ModalDequeEntryParams } from '@store/modules/ui/shared/modal/modalIndex';

import {
    getMessageDetails,
    isModalClosable,
    isModalDismissable,
    shouldShowNewEligibleForCampaignModal,
    shouldShowWelcomeBonusMessage,
} from './messagesHelper';
import { MessageBonusType } from '@src/app/modals/modals/messages/types';
import type { AlertMessageData } from '@core/models/user/pushNotifications/MessageData';
import { featureFlagService } from '@superbet-group/web.lib.flags';
import { getAccountConfig } from '@accountFederation/config/accountConfig';
import PushNotificationsService from '@core/services/pushNotifications/PushNotificationsService';
import { PushEventType } from '@core/services/pushNotifications/types';
import { shouldShowDepositReminder } from '@walletFederation/src/modules/ftdReminder/ftdReminder';
import { isXtremePushLoaded } from '@core/services/inbox/InboxService';
import { createSimpleMutator } from '@superbet-group/web.lib.vue-utilities';
import {
    holdQueuePriorityList,
    IMessagesState,
    IQueueMessage,
    unpausableList,
} from '@store/modules/ui/user/messages/types';
import {
    PUSH_TO_HOLD_QUEUE,
    SET_IS_PAUSED,
} from '@store/modules/ui/user/messages/mutationTypes';
import { KycStatus } from '@accountFederation/modules/idCardVerification/getid/types';
import { getWelcomeOfferModalData } from '@src/app/promotionHub/promotions/utils/helpers';

const ms = require('ms');

/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

const actionCompletedModal = {
    code: modalCodes.error,
    data: {
        text: {
            content: 'This action has already been completed',
        },
    },
};

type Context = ActionContext<Record<string, unknown>, any>;

export function createMessagesModule(
    getArePushNotificationEnabled: (...args: any[]) => boolean,
    subscribeToPushNotifications: (
        callback: (message: Message) => void
    ) => SubscriptionLike | Promise<SubscriptionLike>,
    shouldChooseGdprConsent: (...args: any[]) => boolean,
    userService: {
        fetchMessages: () => Promise<Message[]>;
        respondToMessage: (actionId: number, shouldFetchMoreMessages: boolean) => void;
    },
) {
    function _showGdprMessage({ dispatch, rootGetters }: { dispatch: Dispatch; rootGetters: any }) {
        const user = rootGetters['data/authentication/user'];

        if (shouldChooseGdprConsent(user)) {
            dispatch('ui/modal/addModalToEnd', modalCodes.chooseGdpr, { root: true });
        } else {
            dispatch('showBonusMessages', user);
        }
    }

    return {
        namespaced: true,

        state: {
            isPaused: false,
            holdQueue: [],
        },
        mutations: {
            [SET_IS_PAUSED]: createSimpleMutator<IMessagesState, 'isPaused'>('isPaused'),
            [PUSH_TO_HOLD_QUEUE](state: IMessagesState, queueItem: IQueueMessage): void {
                if (queueItem.type && holdQueuePriorityList.includes(queueItem.type)) {
                    const payloadOrder = holdQueuePriorityList.indexOf(queueItem.type);
                    const nextSuitableSpot = state.holdQueue.findIndex((item, index) => item.type && holdQueuePriorityList.includes(item.type) && index <= payloadOrder);
                    if (nextSuitableSpot === -1) {
                        state.holdQueue.push(queueItem);
                    } else {
                        state.holdQueue.splice(nextSuitableSpot, 0, queueItem);
                    }
                } else {
                    state.holdQueue.push(queueItem);
                }
            },
        },
        actions: {
            showPendingMessages: StoreUtil.asyncErrorGuard((context: Context, isLogin: boolean) => {
                const user = context.rootGetters['data/authentication/user'];
                if (!user) {
                    return;
                }
                if (shellLocalConfig.hasLimitsConsent && !user.limitsConsent) {
                    showLimitInformation(context);
                } else if (isLogin) {
                    _showGdprMessage(context);
                }
            }),
            pauseNotifications({ commit }: { commit: Commit }) {
                commit(SET_IS_PAUSED, true);
            },
            resumeNotifications({ state, commit }: { state: IMessagesState, commit: Commit }) {
                if (!state.isPaused) {
                    return;
                }

                commit(SET_IS_PAUSED, false);
                while (state.holdQueue.length > 0) {
                    const message = state.holdQueue.shift();
                    message?.resume();
                }
            },
            async showBonusMessages(context: Context) {
                if (context.state.isPaused) {
                    context.commit(PUSH_TO_HOLD_QUEUE, {
                        resume: () => _showWelcomeBonusMessage(context),
                    });
                } else {
                    await _showWelcomeBonusMessage(context);
                }

                const processMessage = (message: Message) => {
                    if (context.state.isPaused && (!message.messageType || !unpausableList.includes(message.messageType))) {
                        context.commit(PUSH_TO_HOLD_QUEUE, {
                            type: message.messageType,
                            resume: () => _showMessage(context, message),
                        });
                    } else {
                        _showMessage(context, message);
                    }
                };

                if (getArePushNotificationEnabled(context.rootGetters)) {
                    subscribeToPushNotifications((message) => {
                        processMessage(message);
                    });
                } else {
                    const messages = await userService.fetchMessages();
                    messages.forEach(processMessage);
                }
            },
            // push notification popup should be shown only on second login or if dismissed in 14 days
            async showPushNotification({ dispatch, state }: { dispatch: Dispatch, state: IMessagesState }) {
                if (state.isPaused) {
                    return;
                }

                const isPushNotificationEnabled = await featureFlagService.getFeatureValue('bonus.isPushNotificationEnabled', false);
                const XPdata = JSON.parse(localStorage.getItem('xtremepush.data') || '');
                const pushNotificationDate = localStorage.getItem('pushNotificationDate');
                if (pushNotificationDate === null) {
                    localStorage.setItem('pushNotificationDate', JSON.stringify(Date.now()));
                } else if (Date.now() > JSON.parse(pushNotificationDate)
                    && isPushNotificationEnabled
                    && isXtremePushLoaded()
                    && XPdata
                    && (XPdata.permission === undefined || XPdata.permission === 'default')
                ) {
                    window.xtremePush('ready', () => {
                        dispatch('ui/modal/addModalToEnd', { code: modalCodes.pushNotification }, { root: true });
                    });
                }
            },

            respondAndShowNextMessage: StoreUtil.asyncErrorGuard(
                async (
                    { dispatch, rootGetters }: { dispatch: Dispatch; rootGetters: any },
                    {
                        actionId,
                        shouldShowNextMessage = true,
                        shouldCloseAll = false,
                    }: { actionId: number; shouldShowNextMessage: boolean, shouldCloseAll: boolean },
                ) => {
                    // this method is called from the component
                    // this keeps happening until there are no more messages to show
                    try {
                        const arePushNotificationsEnabled = (
                            rootGetters['data/country/config'] as AppConfig
                        ).user.arePushNotificationsEnabled;
                        const shouldFetchMoreMessages = !arePushNotificationsEnabled;
                        try {
                            await UserService.getInstance().respondToMessage(actionId);
                            if (shouldCloseAll) {
                                dispatch('ui/modal/clearModals', null, { root: true });
                            } else {
                                dispatch('ui/modal/closeModal', null, { root: true });
                            }
                        } catch (e) {
                            dispatch('ui/modal/closeModal', null, { root: true });
                            if (e instanceof NotificationActionError) {
                                dispatch(
                                    'ui/modal/pushModal',
                                    {
                                        code: modalCodes.error,
                                        data: { text: { content: e.message } },
                                    },
                                    { root: true },
                                );
                            }
                        }
                        if (shouldFetchMoreMessages && shouldShowNextMessage) {
                            const messages = await userService.fetchMessages();
                            messages.forEach((message) => dispatch('_showMessage', message));
                        }

                        // makes the caller of this function not wait for balance fetching
                        // wait a few seconds before fetching balance. Tip by Comtrade
                        // eslint-disable-next-line promise/catch-or-return
                        delay(ms('2s')).then(() => {
                            return dispatch(
                                'ui/user/account/getBalance',
                                { shouldCheckBonus: getConfig().countrySpecific.user.bonus.bonusProvider === BonusProvider.Comtrade },
                                { root: true },
                            );
                        });
                        return;
                    } catch (e) {
                        dispatch('ui/modal/closeModal', null, { root: true });
                        dispatch('ui/modal/pushModal', actionCompletedModal, { root: true });
                    }
                },
            ),
        },
    };
}

async function _showWelcomeBonusMessage({
    dispatch,
    rootGetters,
    rootState,
}: {
    dispatch: Dispatch;
    rootGetters: any;
    rootState: any;
}) {
    if (await shouldShowWelcomeBonusMessage(dispatch, rootGetters)) {
        dispatch('ui/modal/addModalToEnd', getWelcomeOfferModalData(rootState.ui.user.bonuses.welcomeBonuses), { root: true });
    } else if (await shouldShowDepositReminder(rootGetters)) {
        dispatch('ui/popover/showFtdReminder', null, { root: true });
    }
}

function _showMessage(
    { dispatch, rootGetters }: { dispatch: Dispatch; rootGetters: any },
    message: Message,
) {
    if (message.messageType === PushMessageType.AlertMessage && message.data) {
        _pushAlert(message as Message & { data: AlertMessageData }, dispatch);
    } else {
        dispatch(
            'ui/modal/addModalToEnd',
            {
                ..._getMessageData(dispatch, rootGetters, message),
                goBack: true,
            },
            { root: true },
        );
    }
}

const showLimitInformation = StoreUtil.asyncErrorGuard(({ dispatch }: { dispatch: Dispatch }) => {
    return dispatch(
        'ui/modal/pushModal',
        {
            code: modalCodes.limitInformation,
            goBack: false,
            isClosable: false,
        },
        { root: true },
    );
}) as AnyFunction;

function _getMessageData(
    dispatch: Dispatch,
    rootGetters: any,
    message: Message,
): ModalDequeEntryParams {
    if (getConfig().countrySpecific.pushNotificationType === PushNotificationType.ServerSentEvent) {
        return _getServerSentMessageData(dispatch, rootGetters, message);
    } else {
        return _getSocketMessageData(dispatch, rootGetters, message);
    }
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function _getServerSentMessageData(
    dispatch: Dispatch,
    rootGetters: any,
    message: Message,
): ModalDequeEntryParams {
    // backward compatibility with old messages
    if (!message.data) {
        return {
            title: message.title,
            data: {
                content: message.content,
                actions: message.actions,
            },
            code: modalCodes.message,
        };
    }

    const isCashBonus
        = (message.data as any).bonusType === MessageBonusType.CashBonus
        || ((message.data as any).bonusType === MessageBonusType.FreeSpins
            && (message.data as any).winnings > 0);

    const modalCode = (() => {
        switch (message.messageType) {
            case PushMessageType.EligibleForCampaign:
                return shouldShowNewEligibleForCampaignModal(message.messageType, message.actions) ? modalCodes.eligibleForCampaignModal : modalCodes.bonusCampaignModal;
            case PushMessageType.BonusAbuser:
                return modalCodes.bonusAbuserModal;
            case PushMessageType.TermsUpdate:
                return modalCodes.termsUpdate;
            case PushMessageType.BrazilMigrationTerms:
                return modalCodes.brazilMigrationTerms;
            case PushMessageType.RealityCheck:
                return modalCodes.realityCheck;
            case PushMessageType.KYCStatusChangedToPassed:
                return _showKycApprovedNotification(dispatch, rootGetters);
            case PushMessageType.CompletedBonus:
                return isCashBonus ? modalCodes.cashBonus : modalCodes.bonusStatusModal;
            default:
                return modalCodes.bonusStatusModal;
        }
    })();
    const modalTitle = (() => {
        switch (message.messageType) {
            case PushMessageType.ExpiredBonus:
                return 'bonus.popup.expired_title';
            case PushMessageType.CompletedBonus:
                return isCashBonus ? 'bonus.popup.cash_bonus_title' : 'bonus.popup.fully-played-title';
            case PushMessageType.NewBonus:
                return 'bonus.popup.received-title';
            case PushMessageType.BonusAbuser:
                return 'bonus.popup.cancelled_title';
            case PushMessageType.TermsUpdate:
                return 'pam.termsUpdateTitle';
            case PushMessageType.RealityCheck:
                return 'pam.realityCheck.title';
            default:
                return '';
        }
    })();
    const messageData = getMessageDetails(message.messageType, message.data);
    return {
        ...(modalTitle ? { title: modalTitle } : {}),
        data: {
            ...message,
            messageData,
            isModalDismissable: isModalDismissable(message.messageType),
        },
        code: modalCode,
        isClosable: isModalClosable(message.messageType, shouldShowNewEligibleForCampaignModal(message.messageType, message.actions)),
    };
}

function _getSocketMessageData(
    dispatch: Dispatch,
    rootGetters: any,
    message: Message,
): ModalDequeEntryParams {
    const title = message.title;
    if (message.transactionalType === PushMessageType.KYCStatusChangedToPassed) {
        const code = _showKycApprovedNotification(dispatch, rootGetters);
        const { title: _omittedTitle, ...data } = message;
        return {
            data,
            code,
        };
    } else if (message.messageType === PushMessageType.TermsUpdate) {
        return {
            title,
            data: message,
            code: modalCodes.message,
            isClosable: false,
            afterClose: () => dispatch('data/authentication/logout', null, { root: true }),
        };
    } else {
        return {
            title,
            data: message,
            code: modalCodes.message,
        };
    }
}

function _showKycApprovedNotification(dispatch: Dispatch, rootGetters: any) {
    const user = rootGetters['data/authentication/user'] as User;
    deleteIdCardVerificationPending(user);
    dispatch(
        'ui/user/notifications/removeNotification',
        NotificationType.idCardVerificationPending,
        { root: true },
    );

    user.isVerifiedIdCard = true;
    if (user.kycDetails) {
        user.kycDetails.status = KycStatus.Verified;
    }

    if (getAccountConfig().hasKycCaptive) {
        dispatch(
            'data/captiveKyc/freeFromCaptiveKyc',
            {
                shouldRedirect: true,
                isUserChoice: false,
            },
            { root: true },
        );
        return modalCodes.verificationKycSuccessful;
    }

    return getAccountConfig().hasGetIdKyc
        ? modalCodes.idCardVerificationApprovedGetId
        : modalCodes.idCardVerificationApproved;
}

function _pushAlert(message: Message & { data: AlertMessageData }, dispatch: Dispatch) {
    const data = message?.data as AlertMessageData;
    if (data?.text || data?.link) {
        const [action] = message.actions;
        dispatch(
            'ui/alerts/pushAlert',
            {
                expires: message.data.expires,
                text: message.data.text,
                link: message.data.link,
                linkText: message.data.linkText,
                type: message.data.type,
                withCloseIcon: message.data.withCloseIcon,
                onRemove: () => UserService.getInstance().respondToMessage(action.id),
                removeOnLinkClick: true,
            },
            { root: true },
        );
    }
}

export default createMessagesModule(
    (rootGetters: any) => {
        const serverConfig: AppConfig = rootGetters['data/country/config'];
        return serverConfig.user.arePushNotificationsEnabled;
    },
    (callback) => {
        return PushNotificationsService.getInstance().subscribe(PushEventType.Message, callback);
    },
    shouldChooseGdprConsent,
    {
        fetchMessages() {
            return UserService.getInstance().fetchMessages();
        },
        respondToMessage(actionId: number) {
            return UserService.getInstance().respondToMessage(actionId);
        },
    },
);
